import React, { useEffect, useRef, useState, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import { useData } from '../../contexts/DataContext';
import { EndeavorApi } from '../../services/api/EndeavorApi';
import ProgressBar from './ProgressBar';
import { useCourseProgress } from './useCourseProgress';
import JoinCourseModal from '../modals/JoinCourseModal';
import './CoursesColumn.css';
import MyCoursesEmptyState from './MyCoursesEmptyState';
import MyCourseInvites from './MyCourseInvites';

// CoursesColumn
// --------------------------------------------------
// Renders a vertical, independently scrollable list of courses.
// DATA: Parent supplies an array `courses` with objects shaped similarly
// to those used by existing course/grid components (id, title, code, instructor,
// location/room, startDate, endDate, progressPercent, etc.). We intentionally
// DO NOT fetch here to keep this presentational and testable.
// INTERACTION: Optional `onSelectCourse(course)` prop (no-op if absent).
// ACCESSIBILITY: Each card is a button-like div with role+tabIndex for keyboard nav.
// FUTURE (TODOs inline): filtering, searching, context menu, skeleton states, virtualization.

export default function CoursesColumn({ 
  courses = [], 
  loading = false, 
  selectedCourseId = null, 
  onSelectCourse,
  highlightCourseId = null,
  // Collapse/expand props
  isCollapsed = false,
  isDrawerMode = false,
  isDrawerOpen = false,
  isResizing = false,
  transitionState = 'idle',
  prefersReducedMotion = false,
  onToggleCollapse = null,
  onCloseDrawer = null,
  focusTargetRef = null,
  setLiveMessage = null
}) {
  // Extended premium card variant with progress animation support.
  const navigate = useNavigate();
  const reportedBadgesRef = useRef(new Set());
  const scrollContainerRef = useRef(null);
  const prevProgressRef = useRef({}); // track previous progress to detect increases
  const { getEndeavors } = useSafeData();
  const [fetchedCourses, setFetchedCourses] = useState([]);
  const [fetchedLoading, setFetchedLoading] = useState(false);
  const [fetchedError, setFetchedError] = useState(false);
  const fetchedOnceRef = useRef(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);

  // Stabilize courses array to prevent unnecessary re-renders
  // Only update when course IDs actually change, not when array reference changes
  const stableCourses = useMemo(() => courses, [courses]);

  // Reset the fetched flag when parent courses become available
  // This ensures we don't get stuck showing fetched courses when parent updates
  useEffect(() => {
    if (stableCourses && stableCourses.length > 0) {
      // Parent has courses, clear any fallback state
      setFetchedCourses([]);
      setFetchedLoading(false);
    }
  }, [stableCourses]);

  const handleSelect = (course) => {
    if (onSelectCourse) {
      // Normalize id so downstream logic (selectedCourseId & vanilla view) works for all variants
      if (course && !course.id) {
        const fallbackId = course.endeavorId || course.endeavorID || course.courseId || course.courseID;
        if (fallbackId) {
          try { course.id = fallbackId; } catch { /* ignore readonly */ }
        }
      }
      onSelectCourse(course);
      try {
        UsageEventsTracker.recordQueued({
          eventType: 'COURSE_SELECT',
          payload: { courseId: course.id || course.endeavorId, source: 'scoutiq-sidebar' }
        });
      } catch { /* ignore */ }
    }
  };

  // Scroll selected course into view
  useEffect(() => {
    if (!selectedCourseId || !scrollContainerRef.current) return;
    try {
      const el = scrollContainerRef.current.querySelector(`[data-course-id="${CSS.escape(String(selectedCourseId))}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    } catch (e) {
      console.warn('[CoursesColumn] Error scrolling to selected course:', e);
    }
  }, [selectedCourseId]);

  // Hydrate course-code pills for existing DOM cards in case data is missing
  useEffect(() => {
    try {
      const extractCodeFromTitle = (title) => {
        if (!title) return '';
        const m = title.match(/^\s*([A-Za-z]{1,6}\s?-?\d{2,6}(?:\.\d+)?)/);
        return m && m[1] ? m[1].trim() : '';
      };

      const container = scrollContainerRef.current || document;
      const cards = container.querySelectorAll('.scoutiq-course-card');
      cards.forEach(card => {
        try {
          const dataCode = card.getAttribute('data-course-code') || '';
          const title = card.getAttribute('data-course-title') || card.querySelector('.scoutiq-course-card-header__title')?.textContent || '';
          const pill = card.querySelector('.scoutiq-course-pill');
          let finalCode = dataCode || extractCodeFromTitle(title);
          if (pill) {
            pill.textContent = finalCode || 'N/A';
            pill.classList.toggle('scoutiq-course-pill--na', !finalCode);
            pill.setAttribute('title', finalCode || 'No course code');
          }
        } catch (inner) { /* ignore per-card errors */ }
      });
    } catch (e) {
      console.warn('[CoursesColumn] Pill hydration failed:', e);
    }
  }, [stableCourses.length]);

  // Fallback: if no courses passed via props, lazily fetch from DataContext (endeavors)
  useEffect(() => {
    // Only fetch if we haven't fetched yet AND parent isn't providing courses
    if (fetchedOnceRef.current) return;
    // Don't fetch if parent has courses or is currently loading
    if ((stableCourses && stableCourses.length > 0) || loading) return;
    if (!getEndeavors) return;
    // reset any previous error before attempting
    setFetchedError(false);
    
    let alive = true;
    (async () => {
      try {
        setFetchedLoading(true);
        const data = await getEndeavors();
        if (alive && Array.isArray(data)) setFetchedCourses(data);
      } catch (err) {
        console.warn('[CoursesColumn] Fallback fetch failed:', err);
        if (alive) setFetchedError(true);
      } finally {
        // mark that we've attempted a fetch so we don't endlessly retry
        fetchedOnceRef.current = true;
        if (alive) setFetchedLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [stableCourses, loading, getEndeavors]);

  // Always prefer parent's courses over fetched courses
  const effectiveCourses = (stableCourses && stableCourses.length > 0) ? stableCourses : fetchedCourses;
  const effectiveLoading = loading || (fetchedLoading && (!stableCourses || stableCourses.length === 0));

  // Show skeletons when parent `loading` is true, or when fallback fetch is in-flight
  // but do NOT show skeletons if the fallback fetch errored (e.g., no user id).
  const showSkeletons = loading || (fetchedLoading && !fetchedError && (!effectiveCourses || effectiveCourses.length === 0));
  const skeletonCount = 5;

  const courseCount = effectiveCourses.length;

  // Debug: log effective data used to decide empty/skeleton rendering
  useEffect(() => {
    try {
      // Keep output compact and consistent for copy/paste into support
      // Note: avoid heavy objects to keep console readable
      console.log('[CoursesColumn debug] effectiveCourses.length=', Array.isArray(effectiveCourses) ? effectiveCourses.length : 'n/a',
        'effectiveLoading=', !!effectiveLoading,
        'showSkeletons=', !!showSkeletons,
        'stableCourses.length=', Array.isArray(stableCourses) ? stableCourses.length : 'n/a',
        'fetchedLoading=', !!fetchedLoading);
    } catch (e) { /* ignore */ }
  }, [effectiveCourses, effectiveLoading, showSkeletons, stableCourses, fetchedLoading]);

  // Handle ESC key to close drawer in mobile mode
  useEffect(() => {
    if (!isDrawerMode || !isDrawerOpen || !onCloseDrawer) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onCloseDrawer();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDrawerMode, isDrawerOpen, onCloseDrawer]);

  return (
    <section 
      className={`scoutiq-courses-column ${isResizing ? 'isResizing' : ''}`} 
      aria-label="Courses column"
      data-tour="my-courses-panel"
      data-tutorial="panel-left-wrap"
      ref={focusTargetRef}
    >
      <div className={`scoutiq-courses-card ${isDrawerMode ? 'drawer-mode' : ''}`} data-tutorial="panel-left-content">
        <div className="scoutiq-courses-header">
          <div className="scoutiq-courses-header__left">
            <div className="scoutiq-courses-header__titles">
              <div className="scoutiq-courses-header__title-row">
                <h2 className="scoutiq-courses-header__title" title="My Courses">
                  <svg className="courses-header-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z" fill="var(--color-brand-primary, #0560fd)"/>
                    <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" fill="var(--color-text-primary, #e2e8f0)"/>
                  </svg>
                  My Courses
                </h2>
                <span className="scoutiq-courses-count" aria-label={`Total courses: ${courseCount}`}>{courseCount}</span>
              </div>
            </div>
          </div>
          <button 
            className="ui-btn--icon-circular scoutiq-courses-add-btn-floating" 
            data-tour="add-course-button"
            data-testid="add-course-button"
            aria-label="Add new course"
            title="Add new course"
            onClick={() => navigate('/create-course-wizard')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <button
            className="ui-btn--icon-circular scoutiq-courses-add-btn-floating"
            data-testid="add-goal-button"
            aria-label="Create a self-directed goal or project plan"
            title="Create a goal or project plan"
            onClick={() => navigate('/create-goal-wizard')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9"></circle>
              <circle cx="12" cy="12" r="5"></circle>
              <circle cx="12" cy="12" r="1.5" fill="currentColor"></circle>
            </svg>
          </button>
          <button
            className="ui-btn--icon-circular scoutiq-courses-join-btn"
            data-testid="join-course-button"
            aria-label="Join a course"
            title="Join a course with invite code"
            onClick={() => setJoinModalOpen(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
          </button>
        </div>
        <div className={`panel-content ${transitionState === 'hiding' ? 'isHiding' : transitionState === 'showing' ? 'isShowing' : ''}`}>
        {/* Search bar placeholder removed per request (can be reinstated when functionality is ready) */}
        <MyCourseInvites onChanged={() => { fetchedOnceRef.current = false; setFetchedCourses([]); }} />
        <div className={`scoutiq-course-list scroll-container ${isResizing ? 'isResizing' : ''}`} role="list" ref={scrollContainerRef}>
          {showSkeletons && (
            <div aria-label="Loading courses" className="scoutiq-course-skeleton-list" role="status">
              {Array.from({ length: skeletonCount }).map((_, i) => (
                <div key={i} className="scoutiq-course-card is-skeleton" aria-hidden="true">
                  <div className="scoutiq-course-card__line skel skel-line long" />
                  <div className="scoutiq-course-card__line skel skel-line short" />
                </div>
              ))}
            </div>
          )}
          {!showSkeletons && effectiveCourses.length === 0 && (
            <MyCoursesEmptyState onAddCourse={() => navigate('/create-course-wizard')} />
          )}
          {!showSkeletons && effectiveCourses.map(course => renderCourseCard({ course, handleSelect, selectedCourseId, reportedBadgesRef, prevProgressRef, highlightCourseId }))}
        </div>
        </div>
      </div>
      <JoinCourseModal
        isOpen={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
        onJoined={() => {
          setJoinModalOpen(false);
          // Reset fetch state so courses list refreshes
          fetchedOnceRef.current = false;
          setFetchedCourses([]);
        }}
      />
    </section>
  );
}

// Safe accessor: avoid hook error if provider missing (returns empty object)
function useSafeData() {
  try {
    return useData();
  } catch {
    return {};
  }
}

function renderCourseCard({ course, handleSelect, selectedCourseId, reportedBadgesRef, prevProgressRef, highlightCourseId }) {
  if (!course || typeof course !== 'object') return null;
  const {
    id,
    endeavorId,
    title,
    name,
    endeavorName,
    courseName,
    code,
    shortName,
    instructor,
    instructorName,
    professor,
    professorName,
    location,
    room,
    startDate,
    endDate,
    progressPercent,
    progress
  } = course;

  // Minimal metadata extraction (idempotent)
  const buildMetaMap = () => {
    const map = {};
    const raw = course.metadata || course.metadataItems || course.metadataValues || [];
    raw.forEach(item => {
      const name = (item?.metadataDefinition?.name || item?.metadataName || item?.name || '').toLowerCase();
      const val = item?.value ?? item?.val ?? item?.text;
      if (name && (val || val === 0)) map[name] = val;
    });
    return map;
  };
  const metaMap = buildMetaMap();

  // Extra metadata used for future detail expansion; encoded for vanilla view
  const EXCLUDED_NAMES = new Set([
    'name','course name','title','description','course description',
    'start date','end date','duration','created','updated','total events','total assignments',
    'course location','semester','meeting times','meeting start time','meeting end time','schedule pattern',
    'office hours','course website','website','instructor emails','instructor phone','course code','code','professor','instructor name','instructor'
  ]);
  const rawMetadata = Array.isArray(course.metadata) ? course.metadata : [];
  const extraMeta = [];
  rawMetadata.forEach(item => {
    const key = (item?.metadataName || item?.name || '').trim();
    const lower = key.toLowerCase();
    if(!key || EXCLUDED_NAMES.has(lower)) return;
    const val = (item?.value ?? '').toString().trim();
    if(!val) return;
    extraMeta.push({ name: key, value: val });
  });
  let extraMetaEncoded = '';
  try { if(extraMeta.length) extraMetaEncoded = encodeURIComponent(JSON.stringify(extraMeta.slice(0,50))); } catch { /* ignore */ }

  const displayTitle = title || name || courseName || endeavorName || metaMap['course name'] || 'Untitled Course';
  let displayCode = code || shortName || course.courseCode || metaMap['course code'] || metaMap['code'] || '';
  // Fallback: extract a leading course code pattern from the title if missing (e.g. MKTG8022.01)
  if (!displayCode && displayTitle) {
    const m = displayTitle.match(/^\s*([A-Za-z]{1,6}\s?-?\d{2,6}(?:\.\d+)?)/);
    if (m && m[1]) {
      displayCode = m[1].trim();
    }
  }
  const displayInstructor = professorName || instructorName || instructor || professor || metaMap['professor'] || metaMap['instructor name'] || metaMap['instructor'] || '';
  const loc = room || location || course.courseLocation || course.roomNumber || metaMap['course location'] || metaMap['location'] || '';
  // Display-only formatter for the My Courses card subtitle (do NOT mutate stored data)
  const formatRoomForCard = (rawRoom) => {
    let s = String(rawRoom || '').trim();
    if (!s) return '';
    // Strip leading labels like Room:, Location:, Rm (case-insensitive)
    s = s.replace(/^\s*(?:room|location|rm|r\.m\.?|room:|location:)\s*[\s:.-]*/i, '');
    // Remove trailing parenthetical and anything after
    s = s.replace(/\s*\(.*$/, '');
    // Split on separators and keep first segment (preserve hyphenated room like B-12 by only splitting on space-dash-space)
    const parts = s.split(/\s*(?:;|,|\||–|—|\s-\s)\s*/);
    s = (parts[0] || '').trim();
    // Collapse multiple spaces
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  };
  const displayLoc = formatRoomForCard(loc);
  const startMeta = startDate || metaMap['start date'] || metaMap['start_date'];
  const endMeta = endDate || metaMap['end date'] || metaMap['end_date'];
  const dateRange = (startMeta && endMeta) ? `${formatDateShort(startMeta)} – ${formatDateShort(endMeta)}` : null;
  // Build separate meta lines: date range separate.
  const dateLine = dateRange || '';
  const pctRaw = typeof progressPercent === 'number' ? progressPercent : (typeof progress === 'number' ? progress : null);
  const pct = pctRaw != null ? Math.min(100, Math.max(0, pctRaw)) : null;

  const isSelected = selectedCourseId && (
    selectedCourseId === id
    || selectedCourseId === endeavorId
    || selectedCourseId === course.courseId
    || selectedCourseId === course.courseID
    || selectedCourseId === course.endeavorID
  );
  const isHighlighted = highlightCourseId && (
    String(highlightCourseId) === String(id) || String(highlightCourseId) === String(endeavorId)
  );
  // Fire one-time badge exposure analytics (if badges exist for this course)
  const { credits, creditHours, category, type } = course;
  const creditVal = credits || creditHours;
  const hasBadges = creditVal || category || type;
  const courseKey = id || endeavorId;
  if (hasBadges && courseKey && reportedBadgesRef && !reportedBadgesRef.current.has(courseKey)) {
    try {
      UsageEventsTracker.recordQueued({
        eventType: 'COURSE_BADGES',
        payload: { courseId: courseKey, credits: creditVal || null, category: category || type || null }
      });
      reportedBadgesRef.current.add(courseKey);
    } catch { /* ignore */ }
  }

  // Detect progress increase for pulse animation
  const thisId = id || endeavorId || displayTitle;
  let progressPulse = false;
  if (pct != null) {
    const prev = prevProgressRef.current[thisId];
    if (typeof prev === 'number' && pct > prev) {
      progressPulse = true;
    }
    prevProgressRef.current[thisId] = pct;
  }

  return (
    <div
      key={thisId}
      className={"scoutiq-course-card" + (isSelected ? " is-selected" : "") + (isHighlighted ? ' is-highlighted' : '') + (progressPulse ? ' has-progress-pulse' : '')}
  data-course-id={id || endeavorId || course.courseId || course.courseID || course.endeavorID}
      data-testid="course-card"
      data-tour="course-card"
      data-tour-id={`course-card--${thisId}`}
      data-course-title={displayTitle}
      data-course-location={loc || ''}
      data-course-dates={dateLine || ''}
      data-course-professor={displayInstructor || ''}
      data-course-description={course.description || course.overview || metaMap['course description'] || metaMap['description'] || ''}
      data-course-start-date={startMeta || ''}
      data-course-end-date={endMeta || ''}
      data-course-created={course.createdAt || course.created_at || metaMap['created'] || ''}
      data-course-updated={course.updatedAt || course.updated_at || metaMap['updated'] || ''}
      data-course-office-hours={metaMap['office hours'] || ''}
      data-course-semester={metaMap['semester'] || ''}
      data-course-meeting-times={metaMap['meeting times'] || ''}
      data-course-meeting-start={metaMap['meeting start time'] || ''}
      data-course-meeting-end={metaMap['meeting end time'] || ''}
      data-course-schedule-pattern={metaMap['schedule pattern'] || ''}
      data-course-website={metaMap['course website'] || metaMap['website'] || ''}
      data-course-instructor-emails={metaMap['instructor emails'] || ''}
      data-course-instructor-phone={metaMap['instructor phone'] || ''}
      data-course-code={displayCode || ''}
  data-office-hours={metaMap['office hours'] || ''}
  data-course-extra-meta={extraMetaEncoded}
      data-course-syllabus-doc-id={course.syllabusDocId || course.syllabusDocumentId || ''}
      role="listitem"
      tabIndex={0}
      onClick={() => handleSelect(course)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSelect(course)}
    >
      <CourseCardMenu course={course} />
      <div className="scoutiq-course-card__body">
        {/* Row 1: Course Code + Room Number (only if either exists) */}
        {(displayCode || displayLoc) && (
          <div className="scoutiq-course-card-header">
            {displayCode ? (
              <span 
                className="scoutiq-course-pill"
                title={displayCode}
              >
                {displayCode}
              </span>
            ) : null}
            {displayLoc ? <span className="scoutiq-course-room" title={loc}>{displayLoc}</span> : null}
          </div>
        )}

        {/* Row 2: Course Title */}
        <h3 className="scoutiq-course-card-header__title" title={displayTitle}>{displayTitle}</h3>

        {/* Row 3: Professor Name */}
        {displayInstructor && (
          <p className="scoutiq-course-meta" title={displayInstructor}>{displayInstructor}</p>
        )}

        {/* Row 4 & 5: Progress Sections */}
        <CourseProgress course={course} />
      </div>
    </div>
  );
}

/**
 * CourseCardMenu — kebab overflow menu for a course card.
 * Provides quick navigation and a "Share course" action (promote a personal
 * course to a student-shared one so the owner can issue invite codes).
 */
function CourseCardMenu({ course }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const wrapRef = useRef(null);

  const courseId = course && (course.id || course.endeavorId || course.courseId || course.courseID || course.endeavorID);
  const ownershipType = course && course.ownershipType;
  const isShared = ownershipType === 'student_shared' || ownershipType === 'org_owned';
  // Hide "Share course" when the course is already shared or org-owned;
  // show a "Manage course" link to the admin dashboard instead.
  const canShare = !!courseId
    && (!ownershipType || ownershipType === 'personal')
    && !isShared;

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!courseId) return null;

  const stop = (e) => { e.stopPropagation(); };

  const handleToggle = (e) => {
    stop(e);
    setOpen(o => !o);
  };

  const handleOpenFull = (e) => {
    stop(e);
    setOpen(false);
    navigate(`/course/${courseId}`);
  };

  const handleOpenAdmin = (e) => {
    stop(e);
    setOpen(false);
    navigate(`/course/${courseId}/admin`);
  };

  const handleShare = async (e) => {
    stop(e);
    if (sharing) return;
    setSharing(true);
    try {
      const resp = await EndeavorApi.shareEndeavor(courseId);
      if (!resp || !resp.ok) {
        const txt = resp && resp.text ? await resp.text() : `status ${resp && resp.status}`;
        // eslint-disable-next-line no-alert
        alert(`Failed to share course: ${txt}`);
        return;
      }
      setOpen(false);
      // Send the owner to the full course page where the Manage tab now appears
      // for invite-code generation.
      navigate(`/course/${courseId}`);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Failed to share course: ${err && err.message ? err.message : err}`);
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="scoutiq-course-card-menu" ref={wrapRef} onClick={stop} onKeyDown={stop}>
      <button
        type="button"
        className="scoutiq-course-card-menu__btn"
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        aria-label="Course actions"
        title="Course actions"
        onClick={handleToggle}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      {open && (
        <div className="scoutiq-course-card-menu__popover" role="menu">
          <button
            type="button"
            role="menuitem"
            className="scoutiq-course-card-menu__item"
            onClick={handleOpenFull}
          >
            Open full course page
          </button>
          {canShare && (
            <button
              type="button"
              role="menuitem"
              className="scoutiq-course-card-menu__item"
              onClick={handleShare}
              disabled={sharing}
            >
              {sharing ? 'Sharing…' : 'Share course'}
            </button>
          )}
          {isShared && (
            <button
              type="button"
              role="menuitem"
              className="scoutiq-course-card-menu__item"
              onClick={handleOpenAdmin}
            >
              Manage course
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// CourseProgress component - Shows Events + Assignments progress
// Memoized to prevent unnecessary re-renders when course hasn't changed
const CourseProgress = memo(function CourseProgress({ course }) {
  const { eventsPercent, assignmentsPercent, loading } = useCourseProgress(course);
  // courseId available if needed for analytics/debugging
  if (loading) {
    return (
      <div className="scoutiq-course-progress">
        <div className="scoutiq-course-progress-block">
          <label className="scoutiq-course-progress-label">EVENTS</label>
          <div className="scoutiq-course-progress-row">
            <div className="scoutiq-course-progress-bar">
              <div className="scoutiq-course-progress-fill" style={{ width: '0%' }} />
            </div>
            <span className="scoutiq-course-progress-pct">—</span>
          </div>
        </div>
        <div className="scoutiq-course-progress-block">
          <label className="scoutiq-course-progress-label">ASSIGNMENTS</label>
          <div className="scoutiq-course-progress-row">
            <div className="scoutiq-course-progress-bar">
              <div className="scoutiq-course-progress-fill" style={{ width: '0%' }} />
            </div>
            <span className="scoutiq-course-progress-pct">—</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="scoutiq-course-progress">
      {/* Events Progress */}
      <div className="scoutiq-course-progress-block">
        <label className="scoutiq-course-progress-label">EVENTS</label>
        <div className="scoutiq-course-progress-row">
          <ProgressBar percent={eventsPercent} className="events" />
          <span className="scoutiq-course-progress-pct">{eventsPercent}%</span>
        </div>
      </div>
      {/* Assignments Progress */}
      <div className="scoutiq-course-progress-block">
        <label className="scoutiq-course-progress-label">ASSIGNMENTS</label>
        <div className="scoutiq-course-progress-row">
          <ProgressBar percent={assignmentsPercent} className="assignments" />
          <span className="scoutiq-course-progress-pct">{assignmentsPercent}%</span>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom memo: only re-render if course ID changes
  // This prevents re-renders when course object reference changes but ID is same
  const prevId = prevProps.course?.id || prevProps.course?.endeavorId || prevProps.course?.courseId;
  const nextId = nextProps.course?.id || nextProps.course?.endeavorId || nextProps.course?.courseId;
  // Return true if IDs are the SAME (meaning "skip update")
  return prevId === nextId;
});

// Badges removed in compact variant per requirements (keep function stub if future use needed)

// Utility: very small date shortener (non-i18n for now)
function formatDateShort(value) {
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.toLocaleString(undefined,{ month:'short' })} ${d.getDate()}`;
  } catch { return ''; }
}
